<template>
  <div
    v-if="!small"
    :class="[
      'mb-8 bg-surface rounded-xl cursor-pointer shadow-sm p-3 pb-16 md:p-6 flex items-center space-x-3 md:space-x-6 w-full max-w-full md:max-w-5xl relative',
      event.event_status === 'cancelled'
        ? 'bg-[repeating-linear-gradient(135deg,_#f4f6fb_0px,_#f4f6fb_10px,_#ffff_10px,_#ffff_20px)] opacity-76'
        : '',
      { 'force-black-text': event.event_status === 'cancelled' && isPriorityShop },
    ]"
    @click="router.push(toEvent(event))">
    <!-- Popular / Not Confirmed Badge -->
    <div v-if="showNotConfirmedWarningBadge" class="absolute -top-2 right-3 md:left-2.5">
      <div
        class="inline-flex gap-1 items-center bg-amber-50 text-amber-800 border border-amber-200 px-2 py-1 rounded-bl-lg relative text-[11px] leading-tight font-medium">
        <ExclamationTriangleIcon class="w-3.5" />
        <span class="md:hidden">{{ $t("May change") }}</span>
        <span class="hidden md:inline">{{ $t("Date not confirmed") }}</span>
      </div>
    </div>
    <div v-else-if="event.is_popular" class="absolute -top-2 right-3 md:left-2.5">
      <div
        class="inline-flex gap-1 items-center bg-popular-tag text-popular-tag-text px-2 py-1 rounded-bl-lg relative text-xs font-semibold">
        <FireIcon class="w-4" />
        <div
          class="absolute bg-gradient-to-tr from-popular-tag from-50% to-transparent from-border-2 border-black to-50% w-2 h-2 -right-2 -top-0"></div>
        {{ $t("Popular") }}
      </div>
    </div>
    <!-- Event date -->
    <div v-if="hasTeams && showDate" class="text-center uppercase min-w-14 md:min-w-16 px-1 mb-6 md:mb-0">
      <div class="text-foreground font-bold md:font-medium text-xs md:text-base">
        {{
          new Date(event.date_start).toLocaleDateString(localeTag, {
            month: "short",
            timeZone: "UTC",
          })
        }}
      </div>
      <div class="text-foreground font-bold text-2xl md:text-[32px]">
        {{ new Date(event.date_start).toLocaleDateString("en-US", { day: "numeric", timeZone: "UTC" }) }}
      </div>
      <div class="text-foreground font-bold md:font-medium text-xs md:text-sm">
        {{ new Date(event.date_start).toLocaleDateString("en-US", { year: "numeric", timeZone: "UTC" }) }}
      </div>
    </div>

    <div v-else class="text-center uppercase min-w-14 md:min-w-16 px-1 mb-6 md:mb-0">
      <CountryFlag :code="event.iso_country" class="max-w-8 max-h-8 mx-auto" />
    </div>

    <!-- Dashed Line -->
    <div class="relative h-20 md:h-28 w-px">
      <div
        class="absolute inset-0 bg-[repeating-linear-gradient(to_bottom,theme(colors.gray.300)_0_12px,transparent_12px_20px)]"></div>
    </div>

    <!-- Match Info -->
    <div class="flex flex-col grow min-w-0 space-y-2.5">
      <!-- Time -->

      <div
        v-if="!isFormula1 && (!!startTime || !event.date_confirmed) && showTime"
        class="hidden md:flex items-center text-foreground text-sm space-x-2">
        <span><ClockIcon class="w-4" /></span>
        <span v-if="event.date_confirmed && startTime && endTime">{{ startTime }} - {{ endTime }}</span>
        <span v-else-if="event.date_confirmed && startTime">{{ startTime }}</span>
        <span v-else class="uppercase">{{ $t("Not confirmed") }}</span>
      </div>

      <div
        v-if="(!!startDate && !!endDate && !hasTeams && showDate) || event.has_sprint_race === true"
        class="hidden md:flex flex-wrap items-center text-foreground text-sm gap-2">
        <template v-if="!!startDate && !!endDate && !hasTeams && showDate">
          <span v-if="startDate === endDate">{{ startDate }}</span>
          <span v-else>{{ eventDatesRange }}</span>
          <span v-if="showNotConfirmedWarningBadge" class="font-medium text-amber-700">
            {{ $t("Date may change") }}
          </span>
          <span v-else-if="!event.date_confirmed" class="uppercase">{{ $t("Not confirmed") }}</span>
        </template>
        <SprintRacePill v-if="event.has_sprint_race === true" />
      </div>

      <!-- Teams -->
      <div v-if="hasTeams" class="md:flex items-center space-x-2 pl-2 md:pl-0 text-lg font-bold md:font-medium">
        <div class="flex items-center space-x-2">
          <!-- Slot is always reserved so the event name starts at the same offset with or without team colors -->
          <svg
            v-if="shouldShowTeamColors"
            class="w-5 h-5 rounded-full overflow-hidden shadow border border-gray-300 flex-none"
            v-html="homeTeam?.colorsSvg"></svg>
          <span v-else class="w-5 h-5 flex-none" aria-hidden="true"></span>
          <span class="text-foreground">{{ event.event_name?.replace(" vs ", " - ") }}</span>
          <svg
            v-if="shouldShowTeamColors"
            class="w-5 h-5 rounded-full overflow-hidden shadow border border-gray-300 flex-none"
            v-html="awayTeam?.colorsSvg"></svg>
        </div>
        <SprintRacePill v-if="event.has_sprint_race === true" class="md:hidden" />
      </div>

      <!-- Event Name -->
      <div v-else class="md:flex items-center space-x-2 pl-2 md:pl-0 text-lg font-bold md:font-medium">
        <div class="flex items-center space-x-2">
          <span class="w-5 h-5 flex-none" aria-hidden="true"></span>
          <span class="text-foreground">{{ event.event_name }}</span>
        </div>
        <div
          v-if="(!!startDate && !!endDate && showDate) || event.has_sprint_race === true"
          class="flex md:hidden flex-wrap items-center text-foreground text-sm gap-2">
          <template v-if="!!startDate && !!endDate && showDate">
            <span v-if="startDate === endDate">{{ startDate }}</span>
            <span v-else>{{ eventDatesRange }}</span>
            <span v-if="showNotConfirmedWarningBadge" class="font-medium text-amber-700">
              {{ $t("Date may change") }}
            </span>
            <span v-else-if="!event.date_confirmed" class="uppercase">{{ $t("Not confirmed") }}</span>
          </template>
          <SprintRacePill v-if="event.has_sprint_race === true" />
        </div>
      </div>

      <div class="flex md:hidden pl-2 md:pl-0 pb-1">
        <span class="text-sm underline cursor-pointer" @click.stop="showInfo = !showInfo">
          <span v-if="!showInfo">{{ $t("More") }}</span>
          <span v-else>{{ $t("Less") }}</span>
          {{ $t("information") }}
        </span>
      </div>

      <div v-show="showInfo" class="flex md:hidden pl-2 md:pl-0 pb-1 text-xs">
        <div>
          {{ event.tournament_name }} {{ event.season }}, {{ event.venue_name }}, {{ event.city }},
          {{ event.iso_country }}
        </div>
      </div>

      <!-- Tags -->
      <div
        class="hidden md:flex flex-wrap gap-2 *:bg-surface-alternate *:text-surface-foreground *:text-sm *:px-3 *:py-1 *:rounded-full">
        <span @click.stop="goToTournament(event)">{{ event.tournament_name }} {{ event.season }}</span>
        <RouterLink :to="eventsWithVenue(slugify(event.venue_name))" @click.stop>{{ event.venue_name }}</RouterLink>
        <RouterLink :to="eventsWithCity(slugify(event.city))" @click.stop>{{ event.city }}</RouterLink>
        <RouterLink :to="eventsWithCountry(event.iso_country)" @click.stop>{{ event.iso_country }}</RouterLink>
      </div>
    </div>

    <!-- Dashed Line -->
    <div class="hidden md:flex relative h-28 w-px">
      <div
        class="absolute inset-0 bg-[repeating-linear-gradient(to_bottom,theme(colors.gray.300)_0_12px,transparent_12px_20px)]"></div>
    </div>

    <div class="hidden md:flex flex-col min-w-44 space-y-4">
      <!-- Price and stock -->
      <div :class="['flex items-center justify-between w-full', !hasPrice ? 'flex-row-reverse' : '']">
        <!-- From and price stacked but centered vertically -->
        <div
          v-if="!['nosale', 'cancelled', 'soldout'].includes(event.event_status)"
          class="flex flex-col items-start justify-center">
          <div v-if="hasPrice">
            <div v-if="hasPriceRange" class="text-sm text-foreground">
              {{ $t("From") }}
            </div>
            <div class="text-lg font-semibold leading-tight">
              {{ currencySymbol }} {{ Math.floor(userStore.calcFx(event.min_ticket_price_eur) / 100) }}
            </div>
          </div>
        </div>

        <!-- Stock -->
        <EventStatus :event />
      </div>

      <!-- Show tickets button -->
      <component
        :is="isB2C ? 'div' : RouterLink"
        v-if="['nosale', 'cancelled'].includes(event.event_status)"
        :to="toEvent(event)"
        @click.stop>
        <Button :disabled="isB2C" variant="outlined" class="w-full gap-1">
          <span v-if="!isB2C"><BellAlertIcon class="w-4" /></span>
          <span>{{ $t(isB2C ? "Not available" : "Request tickets") }}</span>
        </Button>
      </component>

      <component
        :is="isB2C ? 'div' : RouterLink"
        v-else-if="event.event_status === 'soldout'"
        :to="toEvent(event)"
        @click.stop>
        <Button color="neutral" variant="outlined" :disabled="isB2C" class="w-full gap-1">
          <span v-if="!isB2C"><BellAlertIcon class="w-4" /></span>
          <span>{{ $t(isB2C ? "Sold out" : "Request tickets") }}</span>
        </Button>
      </component>

      <RouterLink v-else :to="toEvent(event)" @click.stop>
        <Button class="w-full" data-testid="see-tickets-button">
          <span>{{ $t("See tickets") }}</span>
          <span><ChevronRightIcon class="w-4" /></span>
        </Button>
      </RouterLink>
    </div>

    <div class="absolute -bottom-1 left-0 right-0 md:hidden">
      <hr class="mx-4" />
      <div class="px-4 md:px-6 pb-4 pt-2">
        <div class="flex items-center justify-between">
          <div v-if="!['nosale', 'cancelled', 'soldout'].includes(event.event_status)" class="mt-2 space-x-2">
            <div v-if="hasPrice">
              <span v-if="hasPriceRange" class="text-sm mx-2">{{ $t("From") }}:</span>
              <span class="font-bold text-lg"
                >{{ currencySymbol }} {{ Math.floor(userStore.calcFx(event.min_ticket_price_eur) / 100) }}</span
              >
            </div>
          </div>
          <div class="text-sm mt-2 ml-auto">
            <RouterLink v-if="['nosale', 'cancelled'].includes(event.event_status)" :to="toEvent(event)">
              <Button
                class="w-full flex items-center justify-center gap-2 text-foreground font-semibold rounded-md py-2 bg-transparent border-foreground border-2 !shadow-none">
                <span><BellAlertIcon class="w-4" /></span>
                <span>{{ $t("Request tickets") }}</span>
              </Button>
            </RouterLink>
            <RouterLink v-else-if="event.event_status === 'soldout'" :to="toEvent(event)">
              <Button
                class="w-full flex items-center justify-center gap-2 text-foreground font-semibold rounded-md py-2 bg-transparent border-foreground border-2 !shadow-none">
                <span><BellAlertIcon class="w-4" /></span>
                <span>{{ $t("Request tickets") }}</span>
              </Button>
            </RouterLink>
            <RouterLink v-else :to="toEvent(event)">
              <Button
                class="w-full flex items-center justify-center gap-2 text-btn-forecolor font-semibold rounded-sm py-2 shadow-none!">
                <span>{{ $t("See tickets") }}</span>
              </Button>
            </RouterLink>
          </div>
        </div>
      </div>
    </div>
  </div>
  <RouterLink
    v-else
    :to="toEvent(event)"
    class="bg-surface shadow flex justify-between p-2 gap-2 rounded-lg @container">
    <div class="flex flex-col min-w-0">
      <div>
        <!-- Teams -->
        <div v-if="hasTeams" class="flex flex-wrap items-center space-x-2 @xl:text-lg font-bold">
          <div class="flex items-center space-x-1">
            <svg
              v-if="shouldShowTeamColors"
              class="w-5 h-5 rounded-full overflow-hidden shadow border border-gray-300"
              v-html="homeTeam?.colorsSvg"></svg>
            <span class="text-foreground text-nowrap">{{ event.hometeam_name }}</span>
          </div>
          <span class="text-foreground">-</span>
          <div class="flex items-center space-x-1">
            <svg
              v-if="shouldShowTeamColors"
              class="w-5 h-5 rounded-full overflow-hidden shadow border border-gray-300"
              v-html="awayTeam?.colorsSvg"></svg>
            <span class="text-foreground text-nowrap">{{ event.visiting_name }}</span>
          </div>
        </div>

        <!-- Event Name -->
        <div v-else class="items-center space-x-2 @xl:text-lg font-bold">
          <div class="flex items-center space-x-1">
            <span class="text-foreground">{{ event.event_name }}</span>
          </div>
        </div>
      </div>
      <div class="flex text-muted-foreground items-center gap-x-1 flex-wrap">
        <div v-if="showDate" class="flex items-center gap-1">
          <CalenderIcon class="size-4 flex-none" />
          <div v-if="!!startDate && !!endDate" class="text-sm">
            <span v-if="startDate === endDate">{{ startDate }}</span>
            <span v-else>{{ eventDatesRange }}</span>
            {{ " " }}
            <span v-if="showNotConfirmedWarningBadge" class="font-medium text-amber-700">
              ({{ $t("Date may change") }})
            </span>
            <span v-else-if="!event.date_confirmed" class="uppercase">({{ $t("Not confirmed") }})</span>
          </div>
        </div>
        <SprintRacePill v-if="event.has_sprint_race === true" />
        <div class="flex items-center gap-1">
          <TrophyIcon class="size-4 size-4 flex-none" /> {{ event.tournament_name }} {{ event.season }}
        </div>
      </div>
    </div>
    <div class="flex items-end gap-3 flex-none">
      <div class="hidden @xl:block">
        <EventStatus :event />
      </div>

      <!-- Price -->
      <div
        v-if="!['nosale', 'cancelled', 'soldout'].includes(event.event_status) && event.number_of_tickets"
        class="hidden @lg:flex flex-col">
        <span class="text-sm text-muted-foreground">{{ $t("From") }}</span>
        <span class="font-bold text-lg">{{ formatCurrency(userStore.calcFx(event.min_ticket_price_eur), "EUR") }}</span>
      </div>
      <Button class="size-10 flex items-center justify-center"><ChevronRightIcon class="size-4" /></Button>
    </div>
  </RouterLink>
</template>

<script setup>
import { ChevronRightIcon, ClockIcon, BellAlertIcon, FireIcon, ExclamationTriangleIcon } from "@heroicons/vue/24/solid";
import { useUserStore } from "@/store/user.js";
import { formatTime, formatCurrency, formatDate } from "@xs2event/js-lib";
import { Button } from "@xs2event/js-lib/components";
import { slugify } from "/src/helpers/format.js";
import { formatDates } from "@xs2event/js-lib";
import { computed, ref } from "vue";
import { useEventStore } from "@/store/event.js";
import { toEvent } from "@/components/event/index.js";
import { currencies } from "@xs2event/js-lib";
import { isB2C, isPriorityShop } from "@/brandconfig.ts";
import { eventsWithVenue, eventsWithCity, eventsWithCountry, eventsWithTournament } from "@/constants/routes";
import CountryFlag from "@/components/CountryFlag.vue";
import CalenderIcon from "~icons/ic/baseline-calendar-month";
import TrophyIcon from "~icons/ic/baseline-emoji-events";
import { RouterLink, useRouter } from "vue-router";
import EventStatus from "./EventStatus.vue";
import SprintRacePill from "./SprintRacePill.vue";
import { SportType } from "@xs2event/js-lib";
import { useI18n } from "vue-i18n";
import { DateVisibility } from "@xs2event/js-lib/api";

const router = useRouter();
const userStore = useUserStore();
const eventStore = useEventStore();
const { locale } = useI18n();

const { event } = defineProps({
  event: { required: true },
  small: { type: Boolean, default: false },
});

const showDate = computed(() => {
  return event.date_visibility !== DateVisibility.Hidden;
});

const showTime = computed(() => {
  return event.date_visibility === DateVisibility.ShowDatetime;
});

// date_start/date_stop are venue-local, not UTC — js-lib appends a "Z" regardless.
// formatTime slices the string directly (immune to the trailing "Z"); formatDate parses
// via `new Date()`, so pass `{ timezone: "UTC" }` to read the venue-local wall clock back out.
const startTime = formatTime(event.date_start);
const endTime = formatTime(event.date_stop);
const startDate = formatDate(event.date_start, { timezone: "UTC" });
const endDate = formatDate(event.date_stop, { timezone: "UTC" });

const eventDatesRange = formatDates(event.date_start, event.date_stop, { timezone: "UTC" });

const showInfo = ref(false);

const hasPriceRange = computed(() => {
  return event.min_ticket_price_eur !== null && event.max_ticket_price_eur !== null;
});

const hasPrice = computed(() => {
  return event.min_ticket_price_eur !== null || event.max_ticket_price_eur !== null;
});

const goToTournament = (event) => {
  eventStore.getTournamentById(event.tournament_id).then(async (res) => {
    const tournamentSlug = await res.slug;
    router.push(eventsWithTournament(tournamentSlug));
  });
};

const hasTeams = computed(() => {
  return event.hometeam_name && event.visiting_name;
});

const showNotConfirmedWarningBadge = computed(() => {
  return !event.date_confirmed && !hasTeams.value;
});

const isSoccerSport = computed(() => {
  return event.sport_type === "soccer" || event.sport_type === "football" || event.sport_type === SportType.FOOTBALL;
});

const homeTeam = computed(() => event.hometeam);
const awayTeam = computed(() => event.visiting);

const shouldShowTeamColors = computed(() => {
  return isSoccerSport.value && hasTeams.value && homeTeam.value?.colorsSvg && awayTeam.value?.colorsSvg;
});

const currency = computed(() => currencies.find((c) => c.code === userStore.selectedCurrency));

const currencySymbol = computed(() => currency.value?.symbol);

const isFormula1 = computed(() => event.sport_type === SportType.FORMULA1);

const localeTag = computed(() => locale.value?.replace("_", "-") || "en-US");
</script>

<style>
.force-black-text div,
.force-black-text span {
  color: black !important;
}
</style>
